import React, { useState, useEffect } from 'react';
import { db, auth, functions } from './firebase';
import { collection, doc, getDoc, getDocs, orderBy, query, limit, setDoc, addDoc, serverTimestamp, increment } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import * as Icons from 'react-icons/fa';

// --- PREMIUM FINTECH THEME ---
const THEME = {
  primary: '#0f172a',     // Slate 900
  accent: '#3b82f6',      // Blue 500
  success: '#10b981',     // Emerald 500
  bg: '#f8fafc',          // Slate 50
  card: '#ffffff',        
  textMain: '#1e293b',    // Slate 800
  textSub: '#64748b',     // Slate 500
  border: '#e2e8f0',      
  gradBalance: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
  gradCard: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
};

// --- INJECT CUSTOM CSS ---
const injectStyles = () => {
  if (document.getElementById('billing-styles')) return;
  const style = document.createElement('style');
  style.id = 'billing-styles';
  style.innerHTML = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
    .billing-container { font-family: 'Plus Jakarta Sans', sans-serif; }
    
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .animate-up { animation: fadeUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
    
    .premium-card {
      background: white; border-radius: 16px; border: 1px solid rgba(226, 232, 240, 0.8);
      box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.04); transition: all 0.3s ease;
    }
    .premium-card:hover { box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08); transform: translateY(-2px); }
    
    .tab-btn { transition: all 0.2s ease; cursor: pointer; }
    .tab-btn:hover { background: #f1f5f9; }
    
    .preset-btn { transition: all 0.2s; cursor: pointer; }
    .preset-btn:hover { border-color: ${THEME.accent}; background: #eff6ff; color: ${THEME.accent}; }
    
    .hide-scroll::-webkit-scrollbar { display: none; }
  `;
  document.head.appendChild(style);
};

function BillingAnalytics() {
  const [walletBalance, setWalletBalance] = useState(0.00);
  const [transactions, setTransactions] = useState([]);
  const [businessInfo, setBusinessInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Layout State
  const [activeTab, setActiveTab] = useState('overview'); // overview, methods, history
  
  // Recharge State
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const PRESET_AMOUNTS = [500, 1000, 2000, 5000];

  useEffect(() => {
    injectStyles();
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);

    const fetchBillingData = async () => {
      const user = auth.currentUser;
      if (!user) return;
      
      try {
        const billingDoc = await getDoc(doc(db, `businesses/${user.uid}/settings/billing`));
        if (billingDoc.exists()) setWalletBalance(billingDoc.data().balance || 0);

        const bizDoc = await getDoc(doc(db, `businesses/${user.uid}`));
        if (bizDoc.exists()) setBusinessInfo(bizDoc.data());

        const transQuery = query(collection(db, `businesses/${user.uid}/transactions`), orderBy('timestamp', 'desc'), limit(10));
        const transSnap = await getDocs(transQuery);
        const transList = [];
        transSnap.forEach(doc => transList.push({ id: doc.id, ...doc.data() }));
        setTransactions(transList);

      } catch (e) {
        console.error("Billing Sync Error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchBillingData();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadRazorpay = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleAddFunds = async (amountInput) => {
    const user = auth.currentUser;
    if (!user) return alert("You must be logged in to add funds.");

    const amountNum = Number(amountInput || rechargeAmount);
    if (!amountNum || amountNum < 100) return alert("Minimum recharge is ₹100");
    setIsProcessing(true);

    const isLoaded = await loadRazorpay();
    if (!isLoaded) {
      alert("Razorpay failed to load. Check your internet connection.");
      setIsProcessing(false);
      return;
    }

    try {
      const createOrder = httpsCallable(functions, 'createWalletRechargeOrder');
      const order = await createOrder({ amount: amountNum });

      if (!order || !order.data || !order.data.id) throw new Error("Backend failed to create order.");

      const options = {
        key: 'YOUR_RAZORPAY_KEY_ID', // IMPORTANT: Replace with LIVE Key
        amount: order.data.amount,
        currency: order.data.currency,
        name: 'Tesla CRM Wallet',
        description: 'Account Recharge',
        order_id: order.data.id,
        handler: async function (response) {
          try {
             await setDoc(doc(db, `businesses/${user.uid}/settings/billing`), {
                balance: increment(amountNum)
             }, { merge: true });

             const newTx = {
                amount: amountNum,
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                status: "Success",
                type: "Recharge",
                timestamp: serverTimestamp(),
                date: new Date().toLocaleDateString('en-IN')
             };
             await addDoc(collection(db, `businesses/${user.uid}/transactions`), newTx);

             setWalletBalance(prev => prev + amountNum);
             setRechargeAmount('');
             setTransactions(prev => [newTx, ...prev].slice(0, 10));
             
             // Show Success Tab/Modal (Simple alert for now)
             alert(`🎉 Success! ₹${amountNum.toLocaleString()} added to your wallet.`);

          } catch(err) {
              console.error("Database update failed:", err);
              alert("Payment successful but database update failed. Contact support.");
          }
        },
        prefill: {
            name: businessInfo.businessName || "Spa Owner",
            email: user.email || "", 
        },
        theme: { color: THEME.accent },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response){ alert(`Payment Failed: ${response.error.description}`); });
      rzp.open();
      
    } catch (error) {
      console.error("Payment Error:", error);
      alert(`Error: ${error.message}`);
    }
    setIsProcessing(false);
  };

  if (loading) return <div style={{ height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', color:THEME.textSub, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>Loading Billing Data...</div>;

  return (
    <div className="billing-container animate-up" style={{ background: THEME.bg, minHeight: '100vh', padding: isMobile ? '20px' : '40px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '30px' }}>
      
      {/* --- LEFT SIDEBAR (INTERNAL NAVIGATION) --- */}
      <div style={{ width: isMobile ? '100%' : '260px', flexShrink: 0 }}>
        <h1 style={{ margin: '0 0 24px 0', fontSize: '28px', fontWeight: '800', color: THEME.primary, letterSpacing: '-0.5px' }}>Billing</h1>
        
        <div className="hide-scroll" style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', gap: '8px', overflowX: isMobile ? 'auto' : 'visible', paddingBottom: isMobile ? '10px' : '0' }}>
          {[
            { id: 'overview', icon: <Icons.FaChartPie />, label: 'Overview & Top Up' },
            { id: 'methods', icon: <Icons.FaCreditCard />, label: 'Payment Methods' },
            { id: 'history', icon: <Icons.FaFileInvoiceDollar />, label: 'Billing History' },
          ].map(tab => (
            <div 
              key={tab.id} 
              className="tab-btn"
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderRadius: '12px',
                background: activeTab === tab.id ? 'white' : 'transparent',
                color: activeTab === tab.id ? THEME.accent : THEME.textSub,
                fontWeight: activeTab === tab.id ? '700' : '600',
                fontSize: '14px', whiteSpace: 'nowrap',
                boxShadow: activeTab === tab.id ? '0 2px 10px rgba(0,0,0,0.02)' : 'none',
                border: activeTab === tab.id ? `1px solid ${THEME.border}` : '1px solid transparent'
              }}
            >
              <div style={{ fontSize: '18px' }}>{tab.icon}</div>
              {tab.label}
            </div>
          ))}
        </div>

        {/* Pricing Info (Hidden on mobile to save space, shown in desktop sidebar) */}
        {!isMobile && (
          <div style={{ marginTop: '40px', padding: '20px', background: 'white', borderRadius: '16px', border: `1px solid ${THEME.border}` }}>
            <p style={{ margin: '0 0 15px 0', fontSize: '13px', fontWeight: '800', color: THEME.textMain, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Plan Pricing</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13px', color: THEME.textSub, fontWeight: '500' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Marketing</span> <strong style={{color: THEME.textMain}}>₹ 0.85 / msg</strong></div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Service/Bot</span> <strong style={{color: THEME.textMain}}>₹ 0.35 / msg</strong></div>
            </div>
            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: `1px solid ${THEME.border}`, fontSize: '11px', color: '#94a3b8', lineHeight: '1.5' }}>
              Costs are auto-deducted from your wallet balance per API message sent.
            </div>
          </div>
        )}
      </div>

      {/* --- MAIN CONTENT AREA --- */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
        
        {/* ==========================================
            TAB 1: OVERVIEW & TOP UP 
            ========================================== */}
        {activeTab === 'overview' && (
          <div className="animate-up" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px' }}>
              {/* CURRENT BALANCE CARD (PREMIUM) */}
              <div style={{ background: THEME.gradBalance, padding: '32px', borderRadius: '24px', color: 'white', position: 'relative', overflow: 'hidden', boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.2)' }}>
                <div style={{ position: 'relative', zIndex: 2 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '1px' }}>Available Balance</p>
                    <Icons.FaWallet size={24} color="#94a3b8" />
                  </div>
                  <h1 style={{ margin: '0 0 10px 0', fontSize: '48px', fontWeight: '800', letterSpacing: '-1px' }}>
                    ₹{walletBalance.toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                  </h1>
                  <p style={{ margin: 0, fontSize: '13px', color: walletBalance < 500 ? '#fca5a5' : '#6ee7b7', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {walletBalance < 500 ? <><Icons.FaExclamationTriangle /> Low Balance Warning</> : <><Icons.FaCheckCircle /> Healthy Balance</>}
                  </p>
                </div>
                {/* Decorative Circles */}
                <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '150px', height: '150px', borderRadius: '50%', background: 'rgba(255,255,255,0.03)' }}></div>
                <div style={{ position: 'absolute', bottom: '-20px', right: '40px', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.02)' }}></div>
              </div>

              {/* QUICK RECHARGE CARD */}
              <div className="premium-card" style={{ padding: '32px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ margin: '0 0 5px 0', fontSize: '18px', fontWeight: '800', color: THEME.textMain }}>Quick Top-Up</h3>
                  <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: THEME.textSub, fontWeight: '500' }}>Select an amount or enter custom.</p>
                  
                  {/* Preset Buttons */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                    {PRESET_AMOUNTS.map(amt => (
                      <div 
                        key={amt} 
                        onClick={() => handleAddFunds(amt)}
                        className="preset-btn"
                        style={{ padding: '12px', textAlign: 'center', borderRadius: '10px', border: `1px solid ${THEME.border}`, fontSize: '14px', fontWeight: '700', color: THEME.textMain }}
                      >
                        + ₹{amt.toLocaleString()}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Custom Amount Form */}
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <span style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: THEME.textSub, fontWeight: 'bold' }}>₹</span>
                    <input 
                      type="number" 
                      placeholder="Custom Amount" 
                      value={rechargeAmount}
                      onChange={(e) => setRechargeAmount(e.target.value)}
                      style={{ width: '100%', padding: '14px 14px 14px 35px', borderRadius: '12px', border: `1px solid ${THEME.border}`, fontSize: '14px', fontWeight: '600', outline: 'none', background: THEME.bg, boxSizing: 'border-box' }}
                    />
                  </div>
                  <button 
                    onClick={() => handleAddFunds()} 
                    disabled={isProcessing || !rechargeAmount} 
                    style={{ background: THEME.primary, color: 'white', border: 'none', padding: '0 24px', borderRadius: '12px', fontWeight: '700', cursor: (isProcessing || !rechargeAmount) ? 'not-allowed' : 'pointer', opacity: (isProcessing || !rechargeAmount) ? 0.7 : 1, transition: 'all 0.2s' }}
                  >
                    {isProcessing ? 'Wait...' : 'Add'}
                  </button>
                </div>
              </div>
            </div>

            {/* MINI ACTIVITY TABLE */}
            <div className="premium-card" style={{ padding: '32px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: THEME.textMain }}>Recent Activity</h3>
                <span onClick={() => setActiveTab('history')} style={{ fontSize: '13px', color: THEME.accent, fontWeight: '700', cursor: 'pointer' }}>View All</span>
              </div>
              
              {transactions.length === 0 ? (
                <div style={{ padding: '40px 0', textAlign: 'center', color: THEME.textSub, fontWeight: '500' }}>No transactions yet.</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '500px' }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${THEME.bg}` }}>
                        <th style={{ padding: '12px 0', color: THEME.textSub, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Transaction</th>
                        <th style={{ padding: '12px 0', color: THEME.textSub, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date</th>
                        <th style={{ padding: '12px 0', color: THEME.textSub, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.slice(0, 3).map((tx, idx) => (
                        <tr key={idx} style={{ borderBottom: `1px solid ${THEME.bg}` }}>
                          <td style={{ padding: '16px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: THEME.success + '15', color: THEME.success, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Icons.FaArrowDown size={14} />
                            </div>
                            <div>
                              <div style={{ fontWeight: '700', fontSize: '14px', color: THEME.textMain }}>Wallet Recharge</div>
                              <div style={{ fontSize: '12px', color: THEME.textSub }}>Razorpay ID: {tx.paymentId?.substring(0, 8)}...</div>
                            </div>
                          </td>
                          <td style={{ fontSize: '13px', color: THEME.textSub, fontWeight: '500' }}>{tx.date}</td>
                          <td style={{ textAlign: 'right', fontWeight: '800', fontSize: '15px', color: THEME.success }}>
                            + ₹{Number(tx.amount).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==========================================
            TAB 2: PAYMENT METHODS 
            ========================================== */}
        {activeTab === 'methods' && (
          <div className="animate-up" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <div className="premium-card" style={{ padding: '32px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                  <h3 style={{ margin: '0 0 5px 0', fontSize: '20px', fontWeight: '800', color: THEME.textMain }}>Payment Methods</h3>
                  <p style={{ margin: 0, fontSize: '13px', color: THEME.textSub, fontWeight: '500' }}>Manage cards for fast top-ups.</p>
                </div>
                <button onClick={() => alert("Card vaulting is managed directly during your next Razorpay checkout. Check 'Save Card' on the payment screen.")} style={{ background: THEME.accent, color: 'white', border: 'none', padding: '10px 16px', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icons.FaPlus /> Add New
                </button>
              </div>

              {/* Mock Saved Card UI */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '20px' }}>
                
                {/* Visual Credit Card */}
                <div style={{ background: THEME.gradCard, borderRadius: '16px', padding: '24px', color: 'white', height: '180px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: '0 10px 20px rgba(59, 130, 246, 0.3)', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
                    <Icons.FaMicrochip size={32} color="rgba(255,255,255,0.8)" />
                    <Icons.FaCcVisa size={40} color="white" />
                  </div>
                  <div style={{ position: 'relative', zIndex: 2 }}>
                    <p style={{ margin: '0 0 5px 0', fontSize: '18px', fontWeight: '600', letterSpacing: '2px', fontFamily: 'monospace' }}>**** **** **** 4242</p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                      <div>
                        <p style={{ margin: 0, fontSize: '10px', textTransform: 'uppercase', opacity: 0.8 }}>Card Holder</p>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', textTransform: 'uppercase' }}>{businessInfo.businessName || "Business Owner"}</p>
                      </div>
                      <div>
                        <p style={{ margin: 0, fontSize: '10px', textTransform: 'uppercase', opacity: 0.8 }}>Expires</p>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>12/28</p>
                      </div>
                    </div>
                  </div>
                  {/* Decorative Elements */}
                  <div style={{ position: 'absolute', right: '-20px', bottom: '-20px', width: '150px', height: '150px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }}></div>
                </div>

                {/* Add New Placeholder */}
                <div onClick={() => alert("Open Razorpay to add funds and save a new card.")} style={{ border: `2px dashed ${THEME.border}`, borderRadius: '16px', height: '180px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: THEME.textSub, cursor: 'pointer', transition: 'all 0.2s', background: 'transparent' }} onMouseOver={e => e.currentTarget.style.background = '#f8fafc'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: THEME.border, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', color: THEME.textMain }}>
                    <Icons.FaPlus size={20} />
                  </div>
                  <p style={{ margin: 0, fontWeight: '700', fontSize: '14px', color: THEME.textMain }}>Add Payment Method</p>
                  <p style={{ margin: '5px 0 0 0', fontSize: '12px' }}>Securely via Razorpay</p>
                </div>

              </div>
            </div>
          </div>
        )}

        {/* ==========================================
            TAB 3: BILLING HISTORY 
            ========================================== */}
        {activeTab === 'history' && (
          <div className="premium-card animate-up" style={{ padding: '32px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{ margin: '0 0 5px 0', fontSize: '20px', fontWeight: '800', color: THEME.textMain }}>Billing History</h3>
                <p style={{ margin: 0, fontSize: '13px', color: THEME.textSub, fontWeight: '500' }}>Download receipts and view past recharges.</p>
              </div>
              <button style={{ background: 'white', color: THEME.textMain, border: `1px solid ${THEME.border}`, padding: '8px 12px', borderRadius: '8px', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                <Icons.FaDownload /> Export CSV
              </button>
            </div>

            {transactions.length === 0 ? (
              <div style={{ padding: '60px 0', textAlign: 'center', color: THEME.textSub, background: THEME.bg, borderRadius: '12px' }}>
                <Icons.FaFileInvoiceDollar size={40} style={{ opacity: 0.2, marginBottom: '15px' }} />
                <p style={{ margin: 0, fontWeight: '600', fontSize: '14px' }}>No billing history found.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '600px' }}>
                  <thead>
                    <tr style={{ background: THEME.bg }}>
                      <th style={{ padding: '12px 16px', color: THEME.textSub, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderRadius: '8px 0 0 8px' }}>Date</th>
                      <th style={{ padding: '12px 16px', color: THEME.textSub, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Description</th>
                      <th style={{ padding: '12px 16px', color: THEME.textSub, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Transaction ID</th>
                      <th style={{ padding: '12px 16px', color: THEME.textSub, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</th>
                      <th style={{ padding: '12px 16px', color: THEME.textSub, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right', borderRadius: '0 8px 8px 0' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${THEME.border}` }}>
                        <td style={{ padding: '16px', fontSize: '13px', color: THEME.textMain, fontWeight: '600' }}>{tx.date}</td>
                        <td style={{ padding: '16px', fontSize: '13px', color: THEME.textMain, fontWeight: '600' }}>Wallet Recharge</td>
                        <td style={{ padding: '16px', fontSize: '12px', color: THEME.textSub, fontFamily: 'monospace' }}>{tx.paymentId || 'N/A'}</td>
                        <td style={{ padding: '16px' }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: THEME.success + '15', color: THEME.success, padding: '4px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '800' }}>
                            <Icons.FaCheckCircle /> Paid
                          </span>
                        </td>
                        <td style={{ padding: '16px', textAlign: 'right', fontWeight: '800', fontSize: '14px', color: THEME.textMain }}>
                          ₹{Number(tx.amount).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

export default BillingAnalytics;